'use client';

import {
  type FC,
  type PropsWithChildren,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { AGENT_API_URL } from '@/_libs/constants';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '@/_utils/localstorage';
import type { ChatThread } from '@/_types/chat-threads';
import type { MessageType } from '@/_types/ai';
import type { AdminChatThreadProviderProps } from './AdminChatThreadsProvider.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

const AdminChatThreadsContext = createContext<AdminChatThreadProviderProps>({
  threads: [],
  activeThreadId: null,
  isHistoryOpen: false,
  searchQuery: '',
  isInitialized: false,
  sessionExpired: false,
  createNewThread: async () => undefined,
  switchToThread: () => {},
  deleteThread: () => {},
  updateThreadTitle: () => {},
  searchThreads: () => {},
  toggleHistory: () => {},
  addMessageToCurrentThread: () => {},
  updateMessageInCurrentThread: () => {},
  getCurrentThread: () => null,
  getFilteredThreads: () => [],
  resetCurrentThread: () => {},
  clearPersistedData: () => {},
  refreshSession: async () => {},
  setSessionExpired: () => {},
});

const generateThreadId = () =>
  `thread-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Storage keys for persistence
const getStorageKeys = (userId: string | number) => ({
  CHAT_THREADS: `soraban_chat_threads_${userId}`,
  ACTIVE_THREAD_ID: `soraban_active_thread_id_${userId}`,
});

// Helper functions for localStorage persistence
const loadThreadsFromStorage = (key: string): ChatThread[] => {
  try {
    const stored = getLocalStorageItem(key);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    // Convert date strings back to Date objects
    return parsed.map((thread: any) => ({
      ...thread,
      createdAt: new Date(thread.createdAt),
      updatedAt: new Date(thread.updatedAt),
    }));
  } catch (error) {
    console.warn('Failed to load chat threads from localStorage:', error);
    return [];
  }
};

const saveThreadsToStorage = (key: string, threads: ChatThread[]) => {
  try {
    setLocalStorageItem(key, JSON.stringify(threads));
  } catch (error) {
    console.warn('Failed to save chat threads to localStorage:', error);
  }
};

const loadActiveThreadIdFromStorage = (key: string): string | null => {
  try {
    return getLocalStorageItem(key) || null;
  } catch (error) {
    console.warn('Failed to load active thread ID from localStorage:', error);
    return null;
  }
};

const saveActiveThreadIdToStorage = (key: string, threadId: string | null) => {
  try {
    if (threadId) {
      setLocalStorageItem(key, threadId);
    } else {
      removeLocalStorageItem(key);
    }
  } catch (error) {
    console.warn('Failed to save active thread ID to localStorage:', error);
  }
};

const AdminChatThreadsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const { preferences, isLoadingFirmPreferences } =
    useContext(FirmSettingsContext);
  const { user, token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  // Initialize or reset threads based on user login state
  useEffect(() => {
    if (user?.id) {
      const keys = getStorageKeys(user.id);
      const storedThreads = loadThreadsFromStorage(keys.CHAT_THREADS);
      const storedActiveThreadId = loadActiveThreadIdFromStorage(
        keys.ACTIVE_THREAD_ID,
      );

      if (storedThreads.length > 0) {
        // Ensure welcome message includes default examples for older stored threads
        const DEFAULT_WELCOME_EXAMPLES = [
          'Show me all incomplete questionnaires with entity names',
          'Export all incomplete questionnaires to a spreadsheet',
          'What are the open items for Comprehensive Tax Filing for [entity name] in 2024?',
          'Which clients answered that they received a PPP loan in 2024?',
        ];

        const updatedStoredThreads = storedThreads.map((thread) => ({
          ...thread,
          messages: Array.isArray(thread.messages)
            ? thread.messages.map((m: any) =>
                m?.id === 'welcome-1' &&
                (!m.examples || m.examples.length === 0)
                  ? { ...m, examples: DEFAULT_WELCOME_EXAMPLES }
                  : m,
              )
            : thread.messages,
          isActive: thread.id === storedActiveThreadId,
        }));

        setThreads(updatedStoredThreads);

        if (
          storedActiveThreadId &&
          storedThreads.some((t) => t.id === storedActiveThreadId)
        ) {
          setActiveThreadId(storedActiveThreadId);
        } else {
          const firstThread = storedThreads[0];
          if (firstThread) {
            setActiveThreadId(firstThread.id);
          }
        }
      } else {
        // No threads for this user, reset state
        setThreads([]);
        setActiveThreadId(null);
      }
      setIsInitialized(true);
    } else {
      // User is logged out, clear all state
      setThreads([]);
      setActiveThreadId(null);
      setIsInitialized(false);
    }
  }, [user]);

  // Save threads to localStorage whenever they change
  useEffect(() => {
    if (isInitialized && user?.id) {
      const keys = getStorageKeys(user.id);
      saveThreadsToStorage(keys.CHAT_THREADS, threads);
    }
  }, [threads, isInitialized, user]);

  // Save active thread ID to localStorage whenever it changes
  useEffect(() => {
    if (isInitialized && user?.id) {
      const keys = getStorageKeys(user.id);
      saveActiveThreadIdToStorage(keys.ACTIVE_THREAD_ID, activeThreadId);
    }
  }, [activeThreadId, isInitialized, user]);

  const createNewThread = useCallback(async () => {
    if (!preferences?.ai_chat_enabled && !isLoadingFirmPreferences) return;
    if (!user || !token) {
      console.error('User or token not available for creating a session.');
      return;
    }

    // Check if current thread exists and is empty (only has welcome messages)
    const currentThread = threads.find((t) => t.id === activeThreadId);
    const hasUserMessages = currentThread?.messages?.some(
      (msg) => msg.isUser || msg.role === 'user',
    );
    const isCurrentThreadEmpty = !currentThread || !hasUserMessages;

    if (isCurrentThreadEmpty && currentThread) {
      // Reuse the existing empty thread
      setThreads((prev) =>
        prev.map((t) => ({
          ...t,
          isActive: t.id === currentThread.id,
        })),
      );
      setActiveThreadId(currentThread.id);
      setIsHistoryOpen(false);
      return currentThread.id;
    }

    try {
      const response = await fetch(`${AGENT_API_URL}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: String(user.id),
          token,
        }),
      });
      if (response.status === 401) {
        setSessionExpired(true);
        addToast(
          'error',
          'Your session has expired. Please refresh to continue.',
        );
        return; // Don't create a fallback thread, let the UI show session expired state
      }

      if (!response.ok) {
        throw new Error(
          `Failed to create session: ${response.status} ${response.statusText}`,
        );
      }

      const { session_id } = await response.json();

      const newThread: ChatThread = {
        id: generateThreadId(),
        sessionId: session_id,
        title: 'New Chat',
        preview: '',
        time: new Date().toLocaleString(),
        messages: [
          {
            id: 'welcome-1',
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            content:
              'Welcome to Soraban Assistant! I can help you manage clients, track questionnaires, stay on top of deadlines, and answer IRS-related questions for your firm.',
            examples: [
              'Show me all incomplete questionnaires with entity names',
              'Export all incomplete questionnaires to a spreadsheet',
              'What are the open items for Comprehensive Tax Filing for [entity name] in 2024?',
              'Which clients answered that they received a PPP loan in 2024?',
            ],
            role: 'assistant',
            isUser: false,
          },
        ],
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
      };

      setThreads((prev) => {
        const updated = prev.map((t) => ({ ...t, isActive: false }));
        return [newThread, ...updated];
      });
      setActiveThreadId(newThread.id);
      setIsHistoryOpen(false);
      return newThread.id;
    } catch (error) {
      console.error('Error creating new thread:', error);

      // Create a fallback thread without session for offline/development mode
      const fallbackThread: ChatThread = {
        id: generateThreadId(),
        sessionId: undefined, // No session when server is unavailable
        title: 'New Chat (Offline)',
        preview: '',
        time: new Date().toLocaleString(),
        messages: [
          {
            id: 'welcome-1',
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            content:
              'Welcome to Soraban Assistant! I can help you manage clients, track questionnaires, stay on top of deadlines, and answer IRS-related questions for your firm.',
            examples: [
              'Show me all incomplete questionnaires with entity names',
              'Export all incomplete questionnaires to a spreadsheet',
              'What are the open items for Comprehensive Tax Filing for [entity name] in 2024?',
              'Which clients answered that they received a PPP loan in 2024?',
            ],
            role: 'assistant',
            isUser: false,
          },
          {
            id: 'offline-notice',
            time: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            content: '⚠️ AI Assistant is currently unavailable. Please wait.',
            role: 'assistant',
            isUser: false,
          },
        ],
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
      };

      setThreads((prev) => {
        const updated = prev.map((t) => ({ ...t, isActive: false }));
        const newThreads = [fallbackThread, ...updated];

        // Save fallback thread to storage immediately
        if (user?.id) {
          const keys = getStorageKeys(user.id);
          saveThreadsToStorage(keys.CHAT_THREADS, newThreads);
        }
        return newThreads;
      });
      setActiveThreadId(fallbackThread.id);
      if (user?.id) {
        const keys = getStorageKeys(user.id);
        saveActiveThreadIdToStorage(keys.ACTIVE_THREAD_ID, fallbackThread.id);
      }
      setIsHistoryOpen(false);
      return fallbackThread.id;
    }
  }, [
    activeThreadId,
    addToast,
    isLoadingFirmPreferences,
    preferences?.ai_chat_enabled,
    threads,
    token,
    user,
  ]);

  const refreshSession = useCallback(async () => {
    // When session expires (401), the token is no longer valid
    // The only way to recover is to reload the page and re-authenticate
    window.location.reload();
  }, []);

  const clearPersistedData = useCallback(() => {
    if (!user?.id) {
      console.warn('Cannot clear persisted data: user not logged in.');
      return;
    }
    try {
      const keys = getStorageKeys(user.id);
      removeLocalStorageItem(keys.CHAT_THREADS);
      removeLocalStorageItem(keys.ACTIVE_THREAD_ID);
    } catch (error) {
      console.warn('Failed to clear persisted chat data:', error);
    }
  }, [user]);

  const switchToThread = useCallback((threadId: string) => {
    setThreads((prev) =>
      prev.map((t) => ({
        ...t,
        isActive: t.id === threadId,
      })),
    );
    setActiveThreadId(threadId);
    setIsHistoryOpen(false);
  }, []);

  const deleteThread = useCallback(
    async (threadId: string) => {
      const threadToDelete = threads.find((t) => t.id === threadId);
      if (!threadToDelete) return;

      if (threadToDelete.sessionId) {
        if (!user || !token) {
          console.error('User or token not available for deleting a session.');
          return;
        }

        try {
          const response = await fetch(
            `${AGENT_API_URL}/sessions/${threadToDelete.sessionId}?user_id=${user.id}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to delete session: ${response.status} ${response.statusText}`,
            );
          }
        } catch (error) {
          console.error('Error deleting thread:', error);
          // NOTE: In a real app, we'd show a toast notification here.
          return;
        }
      }

      const isDeletingActive = activeThreadId === threadId;
      const remainingThreads = threads.filter((t) => t.id !== threadId);

      if (isDeletingActive) {
        if (remainingThreads.length > 0) {
          const newActiveThread = remainingThreads[0];
          setActiveThreadId(newActiveThread.id);
          setThreads(
            remainingThreads.map((t) => ({
              ...t,
              isActive: t.id === newActiveThread.id,
            })),
          );
        } else {
          setActiveThreadId(null);
          setThreads([]);
        }
      } else {
        setThreads(remainingThreads);
      }
    },
    [threads, activeThreadId, user, token],
  );

  const updateThreadTitle = useCallback((threadId: string, title: string) => {
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId ? { ...t, title, updatedAt: new Date() } : t,
      ),
    );
  }, []);

  const searchThreads = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const toggleHistory = useCallback(() => {
    setIsHistoryOpen((prev) => !prev);
  }, []);

  const addMessageToCurrentThread = useCallback(
    (message: MessageType) => {
      if (!activeThreadId) return;

      setThreads((prev) =>
        prev.map((thread) => {
          if (thread.id === activeThreadId) {
            const updatedMessages = [...thread.messages, message];
            let title = thread.title;
            let preview = thread.preview;

            // Auto-generate title from first user message
            if (
              thread.title === 'New Chat' &&
              message.isUser &&
              message.content
            ) {
              title =
                message.content.length > 50
                  ? message.content.substring(0, 50) + '...'
                  : message.content;
            }

            // Update preview with latest message
            if (message.content) {
              preview =
                message.content.length > 100
                  ? message.content.substring(0, 100) + '...'
                  : message.content;
            }

            return {
              ...thread,
              title,
              preview,
              messages: updatedMessages,
              time: new Date().toLocaleString(),
              updatedAt: new Date(),
            };
          }
          return thread;
        }),
      );
    },
    [activeThreadId],
  );

  const updateMessageInCurrentThread = useCallback(
    (messageId: string, updatedMessage: Partial<MessageType>) => {
      if (!activeThreadId) return;

      setThreads((prev) =>
        prev.map((thread) => {
          if (thread.id === activeThreadId) {
            const messageIndex = thread.messages.findIndex(
              (m) => m.id === messageId,
            );
            if (messageIndex === -1) return thread;

            const updatedMessages = [...thread.messages];
            const currentMessage = updatedMessages[messageIndex];
            const newMessage = {
              ...currentMessage,
              ...updatedMessage,
              updatedAt: new Date(),
            };
            updatedMessages[messageIndex] = newMessage;

            // Update thread preview if this is the last message and content changed
            let preview = thread.preview;
            const isLastMessage = messageIndex === updatedMessages.length - 1;
            if (
              isLastMessage &&
              updatedMessage.content &&
              !updatedMessage.content.includes('streamingCursor')
            ) {
              // Remove HTML tags and streaming cursor for preview
              const cleanContent = updatedMessage.content
                .replace(
                  /<span class="[^"]*streamingCursor[^"]*">.*?<\/span>/g,
                  '',
                )
                .replace(/<[^>]*>/g, '')
                .trim();

              if (cleanContent) {
                preview =
                  cleanContent.length > 100
                    ? cleanContent.substring(0, 100) + '...'
                    : cleanContent;
              }
            }

            return {
              ...thread,
              preview,
              messages: updatedMessages,
              updatedAt: new Date(),
            };
          }
          return thread;
        }),
      );
    },
    [activeThreadId],
  );

  const getCurrentThread = useCallback(() => {
    return threads.find((t) => t.id === activeThreadId) || null;
  }, [threads, activeThreadId]);

  const resetCurrentThread = useCallback(() => {
    if (!activeThreadId) return;

    setThreads((prev) =>
      prev.map((thread) => {
        if (thread.id === activeThreadId) {
          return {
            ...thread,
            title: 'New Chat',
            preview: '',
            messages: [],
            updatedAt: new Date(),
          };
        }
        return thread;
      }),
    );
  }, [activeThreadId]);

  const getFilteredThreads = useMemo(() => {
    if (!searchQuery) return threads;

    const query = searchQuery.toLowerCase();
    return threads.filter(
      (thread) =>
        thread.title.toLowerCase().includes(query) ||
        thread.preview.toLowerCase().includes(query),
    );
  }, [threads, searchQuery]);

  return (
    <AdminChatThreadsContext.Provider
      value={{
        activeThreadId,
        addMessageToCurrentThread,
        clearPersistedData,
        createNewThread,
        deleteThread,
        getCurrentThread,
        getFilteredThreads: () => getFilteredThreads,
        isHistoryOpen,
        isInitialized,
        refreshSession,
        resetCurrentThread,
        searchQuery,
        searchThreads,
        sessionExpired,
        setSessionExpired,
        switchToThread,
        threads,
        toggleHistory,
        updateMessageInCurrentThread,
        updateThreadTitle,
      }}
    >
      {children}
    </AdminChatThreadsContext.Provider>
  );
};

const useAdminChatThreads = () => {
  const context = useContext(AdminChatThreadsContext);

  if (!context) {
    throw new Error('useChatThreads must be used within ChatThreadsProvider');
  }

  return context;
};

export {
  AdminChatThreadsProvider,
  useAdminChatThreads,
  AdminChatThreadsContext,
};
